// The Resources page's printables: a month's prayer timetable and the Ramadan
// timetable as PDFs, made in the browser from the masjid's own times.
import React, { useState } from 'react';
import useMasjidFormat from "../../../hooks/useMasjidFormat";
import { pdf } from '@react-pdf/renderer';
import toast from 'react-hot-toast';
import { CalendarDays, Download, Loader2, Moon } from 'lucide-react';
import { useLazyBulkPrayertimesQuery } from '../../../store/Reducer/prayertimes';
import { useLazyGetHijriCalendarQuery } from '../../../store/Reducer/hijri-calendar';
import { useGetMasjidInformationQuery } from '../../../store/Reducer/user-setting';
import { MonthTimetable, RamadanTimetable } from './timetable-pdf';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const save = async (doc, fileName) => {
    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const slug = (name) => String(name || 'masjid').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const Printables = () => {
    // Times printed in the masjid's format (General Settings).
    const fmt = useMasjidFormat();
    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());
    const [busy, setBusy] = useState(null); // "month" | "ramadan"
    const { data: masjidData } = useGetMasjidInformationQuery();
    const [getMonth] = useLazyBulkPrayertimesQuery();
    const [getHijri] = useLazyGetHijriCalendarQuery();
    const masjid = masjidData?.data;

    const monthPdf = async () => {
        setBusy('month');
        try {
            const res = await getMonth({ year, month }, true).unwrap();
            const days = res?.data?.prayerTimes || [];
            if (!days.length) throw new Error('No prayer times are set up for that month yet.');
            const label = `${MONTHS[month - 1]} ${year}`;
            await save(<MonthTimetable masjid={masjid} monthLabel={label} days={days} jummah={res.data.jummahTimes} time={fmt.time} />,
                `${slug(masjid?.name)}-prayer-times-${year}-${String(month).padStart(2, '0')}.pdf`);
        } catch (err) {
            toast.error(err?.data?.message || err?.message || "Couldn't make the timetable.");
        } finally {
            setBusy(null);
        }
    };

    // The next Ramadan: this year's if it hasn't ended, otherwise next year's.
    // Its dates come from the Hijri calendar with the masjid's adjustment.
    const ramadanPdf = async () => {
        setBusy('ramadan');
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            let ramadan = [];
            for (const y of [today.getFullYear(), today.getFullYear() + 1]) {
                const cal = await getHijri({ year: y, mode: 'get' }, true).unwrap();
                ramadan = (cal?.data || []).filter((d) => Number(d.hijri?.month?.number) === 9);
                const last = ramadan[ramadan.length - 1];
                const [dd, mm, yy] = String(last?.gregorian?.date || '').split('-').map(Number);
                if (ramadan.length && new Date(yy, mm - 1, dd) >= today) break;
                ramadan = [];
            }
            if (!ramadan.length) throw new Error("Couldn't find the dates of the next Ramadan.");

            // Prayer times for every month Ramadan touches.
            const months = [...new Set(ramadan.map((d) => d.gregorian.date.slice(3)))]; // "MM-YYYY"
            const byDate = {};
            let jummah = [];
            for (const mmYYYY of months) {
                const [mm, yy] = mmYYYY.split('-').map(Number);
                const res = await getMonth({ year: yy, month: mm }, true).unwrap();
                jummah = res?.data?.jummahTimes || jummah;
                (res?.data?.prayerTimes || []).forEach((d) => { byDate[d.date?.gregorian?.date] = d; });
            }
            const days = ramadan
                .map((d) => byDate[d.gregorian.date] && { ...byDate[d.gregorian.date], ramadanDay: Number(d.hijri.day) })
                .filter(Boolean);
            if (!days.length) throw new Error('No prayer times are set up for Ramadan yet.');
            const hijriYear = ramadan[0].hijri.year;
            await save(<RamadanTimetable masjid={masjid} yearLabel={`Ramadan ${hijriYear} AH`} days={days} jummah={jummah} time={fmt.time} />,
                `${slug(masjid?.name)}-ramadan-${hijriYear}.pdf`);
        } catch (err) {
            toast.error(err?.data?.message || err?.message || "Couldn't make the Ramadan timetable.");
        } finally {
            setBusy(null);
        }
    };

    const button = (key, onClick, label, ariaLabel) => (
        <button type="button" onClick={onClick} disabled={Boolean(busy)} aria-label={ariaLabel}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
            {busy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {busy === key ? 'Making PDF…' : label}
        </button>
    );

    return (
        <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-lg">
                <h3 className="flex items-center gap-2 font-medium"><CalendarDays className="h-4 w-4" /> Prayer timetable (PDF)</h3>
                <p className="mb-2 text-sm text-gray-500">A month of Athan and Iqamah times, ready to print.</p>
                <div className="flex flex-wrap items-center gap-2">
                    <select aria-label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <select aria-label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                        {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y}>{y}</option>)}
                    </select>
                    {button('month', monthPdf, 'Download', 'Download prayer timetable')}
                </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
                <h3 className="flex items-center gap-2 font-medium"><Moon className="h-4 w-4" /> Ramadan timetable (PDF)</h3>
                <p className="mb-2 text-sm text-gray-500">Sehri, Iftar, prayer and Taraweeh times for the coming Ramadan.</p>
                {button('ramadan', ramadanPdf, 'Download', 'Download Ramadan timetable')}
            </div>
        </div>
    );
};

export default Printables;
